import React from "react";
import {
  CheckBoxOutlineBlankOutlined,
  CheckBoxOutlined,
  FileDownloadOutlined,
} from "@mui/icons-material";
import { Checkbox, FormControlLabel, IconButton, Tooltip } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";

import AppConfirmDialog from "components/Shared/AppConfirmDialog";
import AppForm from "components/Shared/AppForm";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import DateRangeToggle from "components/Shared/DateRangeToggle";



import { useLazyListDriverQuery } from "store/services/driverApi";

import { exportExcelFile } from "utils/exportExcelFile";

const EXCEL_DATA_INITIAL_STATE = {
  dateRange: [],
  driverId: null,
};

const ExportDriverLicenceExcel = ({ isBusy = false, activeTab }) => {
  
  const [showDialog, setShowDialog] = React.useState(false);

  const [showForm, setShowForm] = React.useState(false);

  const [drivers, setDrivers] = React.useState([]);
 

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: EXCEL_DATA_INITIAL_STATE,
  });

  // Use the RTK query for drivers. (Your API returns driver documents with driverLicence fields.)
  const [fetchDriverLicences, {isFetching: loadingDrivers }] =
    useLazyListDriverQuery();

  React.useEffect(() => {
    fetchDriverLicences({})
      .unwrap()
      .then((response) => {
        setDrivers(response.data);
      })
      .catch((error) => {
        console.error("Error fetching driver options", error);
      });
  }, [fetchDriverLicences]);

  const handleDownload = async (data) => {
    const { dateRange, driverId } = data;
    // 
    const payload = {
      fromDate: dateRange[0],
      toDate: dateRange[1],
      licenseStatus: activeTab, 
      ...(driverId && { driverId: driverId.driverId }),
    };

    try {
      const response = await fetchDriverLicences(payload).unwrap();
      if (!response?.data || response.data.length === 0) {
        toast.info("No records found for the selected date range.");
        return;
      }

      // Flatten the data so that each licence record becomes a separate row.
      const licenceRows = response.data.flatMap((driver) => {
        // Determine if driverLicence is an array or a single object.
        const driverLicences = Array.isArray(driver.driverLicence)
          ? driver.driverLicence
          : driver.driverLicence
          ? [driver.driverLicence]
          : [];

        // For each licence, return a row with driver info plus licence details.
        return driverLicences.map((licence) => ({
          "Driver ID": driver.driverId || "N/A",
          "Driver Name": driver.name || "N/A",
          "Driver Email": driver.email || "N/A",
          "Driver Phone": driver.phone || "N/A",
          "Licence Number": licence.licenceNumber || "N/A",
          "Licence ID": licence.licenceId || "N/A",
          "Issue Date": licence.issueDate
            ? new Date(licence.issueDate).toLocaleDateString()
            : "N/A",
          "Expiry Date": licence.expiryDate
            ? new Date(licence.expiryDate).toLocaleDateString()
            : "N/A",
          "Type": licence.type || "N/A",
          "Status": licence.status || "N/A",
        }));
      });

      exportExcelFile({ sheetData: licenceRows, fileName: "DriverLicenceList" });
    } catch (error) {
      toast.error("Error fetching driver licence data: " + error);
    }

    handleReset();
  };

  const handleReset = () => {
    setShowDialog(false);
    reset(EXCEL_DATA_INITIAL_STATE);
  };

  return (
    <>
      <Tooltip title="Download Excel" arrow>
        <IconButton color="success" onClick={() => setShowDialog(true)}>
          <FileDownloadOutlined />
        </IconButton>
      </Tooltip>

      <AppConfirmDialog
        open={showDialog}
        onClose={handleReset}
        onConfirm={handleSubmit(handleDownload)}
        title="Download Driver Licence Excel"
        dialogIcon={() => <FileDownloadOutlined color="success" />}
      >
        {/* Optional: checkbox to enable driver filter */}
        <FormControlLabel
          control={
            <Checkbox
              checked={showForm}
              onChange={(e) => setShowForm(e.target.checked)}
              icon={<CheckBoxOutlineBlankOutlined />}
              checkedIcon={<CheckBoxOutlined />}
            />
          }
          label="Filter by Driver"
        />

        {showForm && (
          <AppForm loading={isBusy}>
            <AppForm.Content>
              <Controller
                name="driverId"
                control={control}
                rules={{ required: "Please select a Driver." }}
                render={({ field: { value, onChange } }) => (
                  <BaseAutocomplete
                    fullWidth
                    label="Driver"
                    required
                    value={value || null}
                    onChange={(_, data) => onChange(data)}
                    // Combine driverId and name for display
                    getOptionLabel={(option) =>
                      `${option.driverId} - ${option.name}` || ""
                    }
                    isOptionEqualToValue={(option, value) =>
                      option._id === value?._id
                    }
                    options={drivers}
                    loading={loadingDrivers}
                    loadingText="Loading drivers..."
                    helperText={errors?.driverId?.message}
                    error={!!errors?.driverId}
                  />
                )}
              />
            </AppForm.Content>
          </AppForm>
        )}

        {/* Date Range Selection */}
        <Controller
          name="dateRange"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <DateRangeToggle {...field} label="Select Date Range" />
          )}
        />
      </AppConfirmDialog>
    </>
  );
};

export default ExportDriverLicenceExcel;
